import type { Feature, FeatureCollection, Position } from "geojson";
import proj4 from "proj4";
import { CadUploadConfigUnit } from "@iventis/domain-model/model/cadUploadConfigUnit";
import { flattenFeatureCoordinates } from "./coordinate-helpers";
import { getCornerPoints } from "./get-feature-collection-corners";
import { CadSourceControlPoint } from "./cad-source-control-point";

export function createFeatureCollection(features: Feature[]): FeatureCollection {
    return { type: "FeatureCollection", features };
}

/** Will find the closest coordinate in the feature collection from the given coordinate  */
export function getClosestCoordinateInFeatureCollection(featureCollection: FeatureCollection, coordinate: Position): Position {
    let currentDiff = Number.POSITIVE_INFINITY;
    let closestCoordinate: Position | null = null;

    featureCollection.features.forEach((feature) => {
        const flattenedCoordinates = flattenFeatureCoordinates(feature);

        flattenedCoordinates.forEach(([long, lat]) => {
            const diff = Math.abs(long - coordinate[0]) + Math.abs(lat - coordinate[1]);
            if (diff < currentDiff) {
                currentDiff = diff;
                closestCoordinate = [long, lat];
            }
        });
    });

    if (closestCoordinate == null) {
        throw new Error("No closest coordinate found");
    }

    return closestCoordinate;
}

/**
 * Transforms a feature collection to fit within a specified bounding box
 * @param featureCollection The input feature collection
 * @param boundingBox Target bounding box as [[minLng, minLat], [maxLng, maxLat]]
 * @returns Scaled feature collection that fits within the bounding box
 */
export function projectTransverseMercatorToEpsg4326(
    featureCollection: FeatureCollection,
    origin,
    featureUnits: CadUploadConfigUnit,
    rotationDegrees: number
): { projectedFeatureCollection: FeatureCollection; controlPoints: CadSourceControlPoint[] } {
    // Calculate current bounds of the feature collection
    const sourceBounds = calculateFeatureCollectionBounds(featureCollection);
    const units = featureUnits ?? estimateDataSetUnits(featureCollection);
    // Get target bounds
    const dataSetCenterX = (sourceBounds.maxX + sourceBounds.minX) / 2;
    const dataSetCenterY = (sourceBounds.maxY + sourceBounds.minY) / 2;

    const sourceControlPoints = getCornerPoints(featureCollection);
    const controlPoints = sourceControlPoints.map(
        (controlPoint) =>
            ({
                cad: controlPoint.coord.geometry.coordinates,
                transformedCad: projectToEpsg4326(
                    controlPoint.coord.geometry.coordinates as Position,
                    dataSetCenterX,
                    dataSetCenterY,
                    origin[0],
                    origin[1],
                    units,
                    rotationDegrees
                ),
                cornerPosition: controlPoint.corner,
            } as CadSourceControlPoint)
    );

    // Create a deep clone of the features to transform
    const transformedFeatures: Feature[] = featureCollection.features.map((feature) => {
        const newFeature = JSON.parse(JSON.stringify(feature)) as Feature;

        // Transform geometry based on type
        switch (feature.geometry.type) {
            case "Point":
                newFeature.geometry = {
                    type: "Point",
                    coordinates: projectToEpsg4326(feature.geometry.coordinates as Position, dataSetCenterX, dataSetCenterY, origin[0], origin[1], units, rotationDegrees),
                };
                break;

            case "LineString":
                newFeature.geometry = {
                    type: "LineString",
                    coordinates: feature.geometry.coordinates.map((coord) =>
                        projectToEpsg4326(coord, dataSetCenterX, dataSetCenterY, origin[0], origin[1], units, rotationDegrees)
                    ),
                };
                break;

            case "Polygon":
                newFeature.geometry = {
                    type: "Polygon",
                    coordinates: feature.geometry.coordinates.map((ring) =>
                        ring.map((coord) => projectToEpsg4326(coord, dataSetCenterX, dataSetCenterY, origin[0], origin[1], units, rotationDegrees))
                    ),
                };
                break;
            default:
                // eslint-disable-next-line no-console
                console.warn(`Feature type not supported: ${feature.geometry.type}`);
        }

        return newFeature;
    });

    return { projectedFeatureCollection: createFeatureCollection(transformedFeatures), controlPoints };
}

/**
 * Calculates the bounds of a feature collection
 */
export function calculateFeatureCollectionBounds(featureCollection: FeatureCollection): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    featureCollection.features.forEach((feature) => {
        const coordinates = flattenFeatureCoordinates(feature);
        coordinates.forEach(([x, y]) => {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        });
    });

    return { minX, minY, maxX, maxY };
}

/**
 * Transform a single coordinate from source bounds to target bounds
 */
function projectToEpsg4326(coord: Position, dataSetCenterX, dataSetCenterY, targetCenterX, targetCenterY, units, rotationDegrees): Position {
    // Define the projection for meters around the bottom-left corner
    const projMeters = proj4(`+proj=tmerc +lat_0=${targetCenterY} +lon_0=${targetCenterX} +datum=WGS84 +units=${cadUploadConfigUnitToProj4Unit(units)} +no_defs`);
    const projWGS84 = proj4("EPSG:4326");
    const shiftedCoord = [coord[0] - dataSetCenterX, coord[1] - dataSetCenterY];

    // Convert rotation angle from degrees to radians
    const rotationRadians = (rotationDegrees * Math.PI) / 180;

    // Apply rotation
    const rotatedCoord = [
        shiftedCoord[0] * Math.cos(rotationRadians) - shiftedCoord[1] * Math.sin(rotationRadians),
        shiftedCoord[0] * Math.sin(rotationRadians) + shiftedCoord[1] * Math.cos(rotationRadians),
    ];
    return proj4(projMeters, projWGS84, rotatedCoord);
}

/**
 * Transform a single coordinate from source bounds to target bounds
 */
export function unProjectFromEpsg4326(coord: Position, dataSetCenterX, dataSetCenterY, targetCenterX, targetCenterY, units): Position {
    // Define the projection for meters around the bottom-left corner
    const projMeters = proj4(`+proj=tmerc +lat_0=${targetCenterY} +lon_0=${targetCenterX} +datum=WGS84 +units=${cadUploadConfigUnitToProj4Unit(units)} +no_defs`);
    const projWGS84 = proj4("EPSG:4326");
    const coords = proj4(projWGS84, projMeters, coord);
    const shiftedCoord = [coords[0] + dataSetCenterX, coords[1] + dataSetCenterY];
    return shiftedCoord;
}

export function estimateDataSetUnits(featureCollection: FeatureCollection): CadUploadConfigUnit {
    const sourceBounds = calculateFeatureCollectionBounds(featureCollection);
    const width = sourceBounds.maxX - sourceBounds.minX;
    const height = sourceBounds.maxY - sourceBounds.minY;

    const maxDimension = Math.max(width, height);

    // Since we do not know the units, we can only make an educated guess based on typical ranges
    if (maxDimension >= 0.01 && maxDimension <= 20) {
        return CadUploadConfigUnit.Kilometres;
    }
    if (maxDimension >= 20 && maxDimension <= 10000) {
        return CadUploadConfigUnit.Metres;
    }
    return CadUploadConfigUnit.Millimetres;
}

function cadUploadConfigUnitToProj4Unit(unit: CadUploadConfigUnit): string {
    switch (unit) {
        case CadUploadConfigUnit.Millimetres:
            return "mm";
        case CadUploadConfigUnit.Centimetres:
            return "cm";
        case CadUploadConfigUnit.Metres:
            return "m";
        case CadUploadConfigUnit.Kilometres:
            return "km";
        case CadUploadConfigUnit.Inches:
            return "in";
        case CadUploadConfigUnit.Feet:
            return "ft";
        default:
            throw new Error(`Unknown unit: ${unit}`);
    }
}
